import { useEffect, useState } from "react";
import isPast from "date-fns/isPast";
import differenceInMilliseconds from "date-fns/differenceInMilliseconds";
import subSeconds from "date-fns/subSeconds";
import useUser from "../../hooks/use-user";
import { AxiosInstance } from "axios";
import TokenHelper from "../../utils/tokenHelper";
import { useRouter } from "next/router";
import { useSegment, useMixpanel } from "@dreambigger/shared/src/hooks";
import InspireAnalytics from "@dreambigger/shared/src/utils/inspire-analytics-helper";

type Props = {
  api: AxiosInstance;
  tokenHelper: TokenHelper;
  children: JSX.Element;
};

function AuthProvider({ api, tokenHelper, children }: Props) {
  const router = useRouter();
  const urlSearchParams = new URLSearchParams(
    (process?.browser && window.location.search) || ""
  );
  const queryParams = Object.fromEntries(urlSearchParams.entries());
  const refreshTokenQueryParam = queryParams.hash;

  const mixpanel = useMixpanel();
  const segment = useSegment();

  // We start off with a state of refreshing=true as we don't want to render
  // the children until we know if we have/need to refresh our token.
  const [refreshingToken, setRefreshingToken] = useState(true);

  // Fetch the user (if we have a token), as soon as possible to get that request loading.
  const [userLoading, { data: user }] = useUser(api, tokenHelper);

  // On load, check if we need to refresh our JWT token.
  useEffect(() => {
    if (refreshTokenQueryParam) {
      // refreshTokenQueryParam is handled by another useEffect below.
      return;
    }

    // Check if we have the means to refresh a token.
    if (!tokenHelper.getRefreshToken()) {
      // We have no refresh token. If we have a JWT and its expired then
      // lets remove it.
      if (tokenHelper.getJwtToken() && tokenHelper.jwtIsExpired()) {
        tokenHelper.removeJwtToken();
      }

      // We have no means to refresh the token.
      setRefreshingToken(false);
      return;
    }

    const refreshToken = tokenHelper.getRefreshToken();
    if (tokenHelper.jwtIsExpired()) {
      // Refresh the token using the refreshToken.
      tokenHelper
        .refreshJwt(refreshToken)
        .then(scheduleTokenRefresh)
        .catch((err) => console.error(err))
        .finally(() => {
          setRefreshingToken(false);
        });

      // Schedule refresh
      return;
    }

    // We have a valid token.
    scheduleTokenRefresh();
    setRefreshingToken(false);
  }, []);

  // Identify the currently logged in user for Mixpanel analytics.
  useEffect(() => {
    if (user) {
      InspireAnalytics.identify({
        id: String(user.id),
        mixpanel,
        segment,
      });
    }
  }, [user]);

  // Check if there is a refreshToken that is passed in the url.
  // If there is then we need to remove it and use it to refresh our token.
  useEffect(() => {
    if (!refreshTokenQueryParam || !process?.browser) {
      // We only want to do this operation when we have a token from the url and we are on the browser
      return;
    }

    const newParams = { ...queryParams };
    delete newParams.hash;

    let queryString = "";
    if (Object.keys(newParams).length > 0) {
      queryString =
        "?" +
        Object.keys(newParams)
          .map((key) => key + "=" + newParams[key])
          .join("&");
    }
    const pathStr = `${router.asPath.split("?")[0]}${
      queryString ? queryString : ""
    }`;

    // Replace the query string with everything except the hash.
    // NOTE: Had to make shallow false otherwise a page that has not been
    // statically generated before breaks when authing in with a hash.
    router.replace(pathStr, undefined, { shallow: false });

    const token = refreshTokenQueryParam;
    tokenHelper
      .refreshJwt(token)
      .then(() => {
        tokenHelper.setRefreshToken(token);
        scheduleTokenRefresh();
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setRefreshingToken(false);
      });
  }, []);

  // Will infinitely manage the refreshing of a token
  const scheduleTokenRefresh = (): undefined | NodeJS.Timeout => {
    const jwtToken = tokenHelper.getJwtToken();
    const refreshToken = tokenHelper.getRefreshToken();
    const tokenData = tokenHelper.decodeJWT(jwtToken);

    // We cannot schedule a token refresh without a token to refresh.
    if (!tokenData) return;

    const expDate = new Date(tokenData?.exp * 1000);

    // Return early if expiration is undefined or expiration is in the past.
    if (isPast(expDate)) {
      tokenHelper
        .refreshJwt(refreshToken)
        .then(scheduleTokenRefresh)
        .catch((err) => console.error(err));
      return;
    }
    const now = new Date();

    // If we are within 30 seconds from expiring, then refresh the token.
    if (tokenHelper.withinExchangeWindow()) {
      tokenHelper
        .refreshJwt(refreshToken)
        .then(scheduleTokenRefresh)
        .catch((err) => console.error(err));
      return;
    }

    // Get the date 1 minute before the token expires.
    const timeToExchangeToken = subSeconds(expDate, 30);

    // Get the time in milliseconds that we have until we need to refresh the token.
    const millisecondsUntilExchange = differenceInMilliseconds(
      timeToExchangeToken,
      now
    );

    // Set a timeout to go off 30 seconds before the token expires.
    setTimeout(() => {
      scheduleTokenRefresh();
    }, millisecondsUntilExchange);
  };

  if (refreshingToken && process?.browser) return null;

  return children;
}

export default AuthProvider;
